import { useEffect, useState } from 'react';

const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);

/**
 * The wait before an OTP can be resent, counted against a deadline rather than by ticks.
 *
 * rabex-ui's useTime subtracts a second per interval tick, and browsers throttle or pause
 * intervals in a background tab — exactly where the user goes to read the SMS — so the timer
 * came back barely moved. Here each tick (and the tab becoming visible again) just re-reads the
 * clock. Only a duration is measured, so the device clock being off doesn't matter.
 *
 * Same shape as useTime's fields the OTP page uses; when `isStart` is false (Google
 * Authenticator, nothing to resend) it stays full and never completes.
 */
const useResendCountdown = (durationMs: number, isStart: boolean) => {
  const [deadline, setDeadline] = useState(() => Date.now() + durationMs);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isStart) return undefined;
    const tick = () => setNow(Date.now());
    const timer = window.setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [isStart]);

  const remainingMs = isStart ? Math.max(0, deadline - now) : durationMs;
  const totalSeconds = Math.ceil(remainingMs / 1000);

  return {
    time: {
      minutes: pad(Math.floor(totalSeconds / 60)),
      seconds: pad(totalSeconds % 60)
    },
    isComplete: isStart && remainingMs === 0,
    reset: () => {
      const start = Date.now();
      setNow(start);
      setDeadline(start + durationMs);
    }
  };
};

export default useResendCountdown;
